import ContextMenu from '@pinecast/common/ContextMenu';
import styled, {focusVisibleRing} from '@pinecast/styles';
import * as React from 'react';
import type {JSX} from 'react';

import {DEFAULT_FONT} from './constants';
import DownChevronMedium from './icons/DownChevronMedium';
import {useLabelContext, useLabelledControl} from './LabelContext';

const Wrapper = styled('div', (_, designColors) => ({
  alignItems: 'center',
  backgroundColor: designColors.contentBackground,
  borderRadius: 4,
  color: designColors.headingBlue,
  display: 'flex',
  fontSize: 14,
  height: 32,
  opacity: 1,
  position: 'relative',
  transition: 'box-shadow 0.2s, opacity 0.2s',
}));
const SelectButton = styled(
  'button',
  ({disabled}: {disabled: boolean}, designColors) => ({
    alignItems: 'center',
    appearance: 'none',
    MozAppearance: 'none',
    WebkitAppearance: 'none',
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: designColors.borderGrayNormal,
    boxShadow: `inset 0 0 0 3px transparent, 0 2px 0 1px rgba(0, 0, 0, 0.06), 0 1px 1px rgba(0, 0, 0, 0.12)`,
    borderRadius: 4,
    color: designColors.contentTextAccent,
    // Centred by the flex box rather than by a line box the height of the
    // control: an option is free to render an element, and an inline one lands
    // on `vertical-align: middle` — half an x-height above the baseline, which
    // is a couple of pixels shy of the middle — rather than in the centre.
    display: 'flex',
    fontFamily: DEFAULT_FONT,
    fontSize: 14,
    fontWeight: 400,
    height: 32,
    lineHeight: 20,
    marginTop: 0,
    marginRight: 0,
    marginBottom: 0,
    marginLeft: 0,
    padding: '0 25px 0 10px',
    textAlign: 'left',
    transition: 'border-color 0.2s, box-shadow 0.2s, opacity 0.2s',
    whiteSpace: 'nowrap',
    width: '100%',

    ':hover': !disabled
      ? {
          // borderColor: designColors.inputBorder,
          boxShadow: `inset 0 0 0 3px ${designColors.borderBlueLight}, 0 2px 0 1px rgba(0, 0, 0, 0.06), 0 1px 1px rgba(0, 0, 0, 0.12)`,
        }
      : undefined,
    ':disabled': {
      opacity: 0.5,
    },

    ...focusVisibleRing(designColors),
  }),
);

export type Option = {
  key: string | number;
  render: () => JSX.Element | string;
};

type Props = {
  'aria-describedby'?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  onChange: (newValue: string | number) => void;
  options: Array<Option>;
  style?: React.CSSProperties;
  tabIndex?: number;
  value: string | number;
};

const SelectCustom = ({
  'aria-describedby': describedBy,
  autoFocus,
  disabled = false,
  onChange,
  options,
  style,
  tabIndex,
  value,
}: Props) => {
  const button = React.useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = React.useState(false);
  const id = React.useId();
  const buttonId = React.useId();

  // Inside a `<Label>`, the button is named after the label *and* what it
  // shows: "Another amount, €3.00 per month". A native label alone would
  // replace the button's text as its name, and a screen reader would say what
  // the select is for but never what it is set to. Outside a label the text is
  // the name already.
  const labelling = useLabelledControl({
    'aria-describedby': describedBy,
    'aria-labelledby': useLabelContext() ? buttonId : undefined,
    id: buttonId,
  });

  const handleClick = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setOpen(true);
    },
    [],
  );

  const handleClose = React.useCallback(() => {
    setOpen(false);
    button.current?.focus();
  }, []);

  const selected = options.find(x => x.key === value);
  if (!selected) {
    throw new Error(`${value} not found in SelectCustom`);
  }
  return (
    <ContextMenu
      id={id}
      keepActiveOnPointerLeave
      // The list is named by the button alone. The default, the whole anchor,
      // holds the `aria-owns` below, so it would read the options out as well.
      labelledBy={buttonId}
      onClose={handleClose}
      onSelect={onChange}
      open={open}
      options={options.map(option => ({
        name: option.render() as any,
        slug: option.key,
      }))}
      // A choice, as a native `select` is: options, with the chosen one selected.
      role="listbox"
      toSelect={selected.key}
      wrapperStyle={style}
    >
      <Wrapper>
        <SelectButton
          {...labelling}
          aria-expanded={open}
          aria-haspopup="listbox"
          autoFocus={autoFocus}
          disabled={disabled}
          onClick={handleClick}
          ref={button}
          tabIndex={tabIndex}
        >
          {selected.render()}
          <DownChevronMedium
            size={10}
            style={{
              position: 'absolute',
              right: 8,
              top: 0,
              bottom: 0,
              margin: 'auto',
            }}
            aria-hidden="true"
          />
        </SelectButton>
        {/* The options render in a portal, and only while open, so the list
            is owned here only then: an `aria-owns` that names a missing
            element is invalid (axe `aria-valid-attr-value`). */}
        {open && <div aria-owns={id} />}
      </Wrapper>
    </ContextMenu>
  );
};

export default SelectCustom;
